<script setup lang="ts">
import { type Ref, computed, ref } from 'vue'
import defaultErrorImg from '@/assets/error.png'
import { isEmpty } from '@/utils/util'

const props = defineProps({
  src: {
    type: String,
    required: true
  },
  aspectRatio: {
    type: Number,
    default: 1
  },
  errorImg: {
    type: String,
    required: false,
    default: defaultErrorImg
  },
  rounded: {
    type: Boolean,
    default: false,
    required: false
  }
})

const loaded = ref(false)
const error = ref(false)

const imgRef: Ref<HTMLImageElement | undefined> = ref()

const aspectRatio = computed(() => props.aspectRatio)

const imgSrc = computed(() => {
  if (loaded.value) {
    return props.src
  } else if (error.value) {
    return errorImg.value
  } else {
    return props.src
  }
})

function onLoad() {
  loaded.value = true
}

function onError() {
  loaded.value = true
  error.value = true
}

const errorImg = computed(() => props.errorImg || defaultErrorImg)
</script>
<template>
  <RatioSafeBlock
    :class="{ 'rounded-full': props.rounded }"
    :ratio="aspectRatio"
    flex="~ col"
    relative
    w-full
    cursor-pointer>
    <img
      ref="imgRef"
      @load="onLoad"
      @error="onError"
      :class="{
        'object-none': isEmpty(imgSrc) || error,
        'object-cover': !(isEmpty(imgSrc) || error),
        'rounded-full': props.rounded
      }"
      :src="imgSrc"
      absolute
      z1
      h-full
      w-full />
    <!-- loading animation -->
    <svg
      class="centered-element"
      v-if="!error && !loaded"
      xmlns="http://www.w3.org/2000/svg"
      width="36"
      height="36"
      viewBox="0 0 24 24">
      <g fill="none" stroke="#D8D8D8" stroke-linecap="round" stroke-width="1.3">
        <path
          stroke-dasharray="60"
          stroke-dashoffset="60"
          stroke-opacity="0.3"
          d="M12 3C16.9706 3 21 7.02944 21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3Z">
          <animate fill="freeze" attributeName="stroke-dashoffset" dur="1.95s" values="60;0" />
        </path>
        <path stroke-dasharray="15" stroke-dashoffset="15" d="M12 3C16.9706 3 21 7.02944 21 12">
          <animate fill="freeze" attributeName="stroke-dashoffset" dur="0.45s" values="15;0" />
          <animateTransform
            attributeName="transform"
            dur="2.25s"
            repeatCount="indefinite"
            type="rotate"
            values="0 12 12;360 12 12" />
        </path>
      </g>
    </svg>
  </RatioSafeBlock>
</template>
<style lang="scss" scoped></style>
